import { reportException } from 'components/sharpr/errorLogger/errorLoggerDecorator';
import { htmlExtractText } from 'components/sharpr/html/extractText/htmlExtractText';
import _ from 'lodash';

// define request and or response interceptors
export function ApiInterceptor($timeout, $injector) {
	'ngInject';
	let isAlerting = false;
	const handleNetworkError = details => {
		const computerOrDevice = window.isMobile ? 'device' : 'computer';
		const homeUrl = window.isMobile ? 'mobile' : 'hub';
		const message = `
			Your ${computerOrDevice} has encountered a network error.
			If the problem persists, <a href="/${homeUrl}">restart the app</a>
			or <a href="mailto:support@sharpr.com?subject=Server%20Error">contact us</a>.
		`;
		const detailsText = `
Unexpected response from:
${details.verb.toUpperCase()} ${details.url}

Content-type:
${details.content_type}

Response:
${details.response_text}`.trim();
		$injector.get('ErrorScreen').show({
			class: 'not-allowed',
			title: 'Network Error',
			message: message,
			actionText: 'Reload',
			actionUrl: '',
			detailsText: detailsText,
			actionCallback: $event => {
				$event.preventDefault();
				window.location.reload(true);
			},
		});
		if (details.verb == 'POST' && details.url == '/api/v2/metrics/events') {
			// don't get in a loop with reporting errors of the error event recorder
			return;
		}
		$injector.get('EventTracker').save('network_error', {
			verb: details.verb,
			url: details.url,
			domain: window.location.hostname,
			is_cloudflare: details.is_cloudflare,
			content_type: details.content_type,
			response_text: details.response_text.slice(0, 1024 * 16),
		});
	};
	return {
		request: config => {
			// Split long headers into chunks because Apache has a max of 8192 bytes per header
			const headerMaxLength = 8000;
			// (e.g. "Hubs" header can get really long for superadmins)
			// see api2->loadRequestHeaders()
			_.forOwn(config.headers, (value, name) => {
				if (!value || typeof value != 'string' || value.length <= headerMaxLength) {
					// header is short
					return;
				}
				// header is too long; chunk it into pieces
				chunkString(value, headerMaxLength).forEach((chunk, i) => {
					config.headers[`${name}-Chunk-${i}`] = chunk;
				});
				config.headers[name] = undefined;
			});
			return config;
		},
		/*
		 * 2 checks:
		 * 	- Check that the device is online
		 *	- Check that we got JSON back if this is an API call
		 */
		response: response => {
			// 1. network connectivity?
			if (
				response.status == -1 &&
				!isAlerting &&
				// see https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-network-information/
				navigator.connection &&
				typeof Connection == 'object' &&
				navigator.connection.type === Connection.NONE
			) {
				isAlerting = true;
				const disconnectedMsg =
					'It seems this device is not connected to the internet. Please try again once the device has reconnected to the network.';
				alert(disconnectedMsg);
				$timeout(() => {
					isAlerting = false;
				}, 5000);
			}
			// we don't need to run this if we know we got a bad response back
			else if (response.status != -1) {
				// 2. API returns JSON?
				let contentType = _.isFunction(response.headers) ? response.headers('Content-Type') || '' : '';
				if (
					response.data !== '' &&
					!contentType.match(/^application\/json/) &&
					!response.config.url.match(/.+\.html($|\?)/)
				) {
					const responseText = String(
						JSON.stringify(typeof response.data == 'string' ? htmlExtractText(response.data) : response.data)
					).slice(0, 6000);
					// error will show up in Sentry
					handleNetworkError({
						verb: response.config.method,
						url: response.config.url,
						content_type: contentType,
						is_cloudflare: /\bCloudFlare\b/.test(responseText) ? 'Yes' : 'No',
						response_text: responseText,
					});
				}
			}
			return response;
		},
	};
}

// https://stackoverflow.com/questions/7033639/split-large-string-in-n-size-chunks-in-javascript
function chunkString(str, len) {
	str = String(str);
	var _size = Math.ceil(str.length / len),
		_ret = new Array(_size),
		_offset;

	for (var _i = 0; _i < _size; _i++) {
		_offset = _i * len;
		_ret[_i] = str.substring(_offset, _offset + len);
	}

	return _ret;
}
